import React, { useLayoutEffect, useRef } from "react";
import { setNavTransitioning } from "../../hooks/navTransition";
import {
  BreadcrumbSlot,
  HeaderSlot,
  MainSlot,
  SecondarySlot,
  ShellContainer,
  SidebarSlot,
} from "./Shell.styles";
import { NAV_DRAWER_WIDTH, NAV_PIN_MS } from "../molecules/NavDrawer/NavDrawer.constants";
import type { ShellProps, ShellVariant } from "./Shell.types";

/**
 * Derives the grid variant from which optional slots are filled.
 * No conditional branches in consumers — pass slots, get the right grid.
 */
export const resolveShellVariant = (
  hasBreadcrumb: boolean,
  hasSecondary: boolean,
): ShellVariant => {
  if (hasBreadcrumb && hasSecondary) return "withBreadcrumbAndSecondary";
  if (hasBreadcrumb) return "withBreadcrumb";
  if (hasSecondary) return "withSecondary";
  return "default";
};

const NAV_TRANSITION_SLACK_MS = 100;

/** Charts re-measure once, when the shelf finishes resizing, not on every frame. */
const onShelfTransitionEnd = (event: React.TransitionEvent<HTMLElement>) => {
  if (event.target === event.currentTarget && event.propertyName === "grid-template-columns") {
    setNavTransitioning(false);
    window.dispatchEvent(new Event("resize"));
  }
};

/**
 * Slot-based app shell. Each page composes the shell by passing slot nodes
 * instead of branching on route paths. The grid template auto-adjusts.
 */
export const Shell: React.FC<ShellProps> = ({
  header,
  sidebar,
  breadcrumb,
  secondary,
  secondaryWidth = "auto",
  sidebarOverlay = false,
  sidebarPinned = false,
  viewport = "desktop",
  main,
  overlays,
}) => {
  const variant = resolveShellVariant(Boolean(breadcrumb), Boolean(secondary));

  // A pin / unpin starts a shelf transition: size observers wait for its end (with a safety timeout,
  // since reduced motion or an interrupted transition may never fire transitionend).
  const firstRun = useRef(true);
  useLayoutEffect(() => {
    if (firstRun.current) {
      firstRun.current = false;
      return undefined;
    }
    if (!sidebarOverlay) return undefined;
    setNavTransitioning(true);
    const timer = setTimeout(() => {
      setNavTransitioning(false);
      window.dispatchEvent(new Event("resize"));
    }, NAV_PIN_MS + NAV_TRANSITION_SLACK_MS);
    return () => clearTimeout(timer);
  }, [sidebarPinned, sidebarOverlay]);

  return (
    <ShellContainer
      $variant={variant}
      $secondaryWidth={secondaryWidth}
      data-layout-variant={variant}
      data-testid="app-shell"
      data-viewport={viewport}
      $sidebarWidth={sidebarOverlay ? (sidebarPinned ? NAV_DRAWER_WIDTH : "0rem") : undefined}
      onTransitionEnd={sidebarOverlay ? onShelfTransitionEnd : undefined}
    >
      <HeaderSlot data-testid="header-slot">{header}</HeaderSlot>
      <SidebarSlot $overlay={sidebarOverlay} data-testid="primary-sidebar-slot">{sidebar}</SidebarSlot>
      {breadcrumb && (
        <BreadcrumbSlot data-testid="breadcrumb-slot">
          {breadcrumb}
        </BreadcrumbSlot>
      )}
      {secondary && (
        <SecondarySlot data-testid="secondary-sidebar-slot">
          {secondary}
        </SecondarySlot>
      )}
      <MainSlot>{main}</MainSlot>
      {overlays}
    </ShellContainer>
  );
};

export default Shell;
